import React, { useCallback, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardHeader, Container } from "reactstrap";
import BreadCrumb from "components/common/BreadCrumb";
import TableContainer from "components/common/TableContainer";
import { ColumnDef } from "@tanstack/react-table";
import { IndexColumn } from "components/common/columns";
import { getDocumentTitle } from "utils/helpers";
import type { StockMovement } from "features/StockMovements/types/stock-movement.types";
import { getStockMovements } from "features/StockMovements/services/StockMovementService";
import {
  StockMovementType,
  StockMovementTypeLabel,
} from "features/StockMovements/constants/stock-movement.constants";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import moment from "moment";
import StockMovementListFilters from "./StockMovementListFilters";
import StockMovementActionsDropdown from "../components/StockMovementActionsDropdown";

const StockMovementList = () => {
  document.title = getDocumentTitle("Stock movements");
  const { can } = useAccess();
  const canCreate = can(Permissions.STOCK_MOVEMENT.CREATE);
  const canRead = can(Permissions.STOCK_MOVEMENT.READ);

  const tableRef = useRef<() => void>(null);
  const [movementTypeFilter, setMovementTypeFilter] = useState<
    StockMovementType | ""
  >("");
  const [productIdFilter, setProductIdFilter] = useState<number | "">("");
  const [locationIdFilter, setLocationIdFilter] = useState<number | "">("");

  const requestParams = useMemo(
    () => ({
      productId: productIdFilter === "" ? undefined : Number(productIdFilter),
      movementType: movementTypeFilter === "" ? undefined : movementTypeFilter,
      locationId:
        locationIdFilter === "" ? undefined : Number(locationIdFilter),
    }),
    [locationIdFilter, movementTypeFilter, productIdFilter],
  );

  const filtersBadgeCount = useMemo(() => {
    let n = 0;
    if (movementTypeFilter) n += 1;
    if (productIdFilter !== "") n += 1;
    if (locationIdFilter !== "") n += 1;
    return n;
  }, [locationIdFilter, movementTypeFilter, productIdFilter]);

  const columns: ColumnDef<StockMovement>[] = useMemo(
    () => [
      IndexColumn<StockMovement>(),
      {
        header: "Date",
        id: "when",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const raw = row.original.createdAt ?? row.original.created_at;
          return raw ? (
            <span className="text-muted">{moment(raw).format("lll")}</span>
          ) : (
            "—"
          );
        },
      },
      {
        header: "Type",
        accessorKey: "movementType",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <span className="badge bg-primary-subtle text-primary rounded-pill">
            {StockMovementTypeLabel[row.original.movementType]}
          </span>
        ),
      },
      {
        header: "Product",
        id: "product",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const p = row.original.product;
          return p ? (
            <Link
              className="fw-medium link"
              to={`/inventory-management/products/${p.id}`}
            >
              {p.name}
            </Link>
          ) : (
            `#${row.original.productId}`
          );
        },
      },
      {
        header: "Qty",
        accessorKey: "quantity",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <span
            className={
              row.original.quantity < 0 ? "text-danger" : "text-success"
            }
          >
            {row.original.quantity}
          </span>
        ),
      },
      {
        header: "From",
        id: "from",
        enableColumnFilter: false,
        cell: ({ row }) =>
          row.original.fromLocation?.name ??
          (row.original.fromLocationId != null
            ? `#${row.original.fromLocationId}`
            : "—"),
      },
      {
        header: "To",
        id: "to",
        enableColumnFilter: false,
        cell: ({ row }) =>
          row.original.toLocation?.name ??
          (row.original.toLocationId != null
            ? `#${row.original.toLocationId}`
            : "—"),
      },
      {
        header: "Reference",
        id: "ref",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const rt = row.original.referenceType;
          const ri = row.original.referenceId;
          if (!rt && ri == null) return "—";
          return (
            <span className="text-muted small">
              {rt ?? "—"}
              {ri != null ? ` #${ri}` : ""}
            </span>
          );
        },
      },
      {
        header: "Actions",
        id: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <StockMovementActionsDropdown
            record={row.original}
            canRead={canRead}
          />
        ),
      },
    ],
    [canRead],
  );

  const clearFilters = useCallback(() => {
    setMovementTypeFilter("");
    setProductIdFilter("");
    setLocationIdFilter("");
  }, []);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Stock movements" pageTitle="Inventory Management" />
        <Card>
          <CardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2 border-bottom">
            <div>
              <h5 className="card-title mb-1">Stock movements</h5>
              <p className="text-muted small mb-0">
                Purchases, issues, transfers, returns, and adjustments.
              </p>
            </div>
            {canCreate ? (
              <Link
                to="/inventory-management/stock-movements/create"
                className="btn btn-primary"
              >
                <i className="ri-add-line align-bottom me-1" />
                Record movement
              </Link>
            ) : null}
          </CardHeader>
          <div className="card-body pt-0">
            <TableContainer
              columns={columns}
              fetchApi={getStockMovements}
              requestParams={requestParams}
              pageSize={10}
              globalSearch={true}
              SearchPlaceholder="Search by product, type, location, reference…"
              refreshRef={tableRef}
              divClass="table-responsive table-card mb-0"
              tableClass="align-middle table-nowrap mb-0"
              theadClass="table-light text-muted"
              toolbarRight={<StockMovementListFilters
                movementTypeFilter={movementTypeFilter}
                productIdFilter={productIdFilter}
                locationIdFilter={locationIdFilter}
                onMovementTypeChange={setMovementTypeFilter}
                onProductIdChange={setProductIdFilter}
                onLocationIdChange={setLocationIdFilter}
              />}
              filterCount={filtersBadgeCount}
              onFiltersClear={clearFilters}
            />
          </div>
        </Card>
      </Container>
    </div>
  );
};

export default StockMovementList;
